import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Actions,
  selectChosenGymID,
  selectChosenSocialEventID,
  selectChosenPin,
} from '@pass-frontend/common/offerings/data-access';
import {
  EventOffering_PassEdge as EventOffering,
  FacilityOffering,
} from '@pass-frontend/data-access/common/edge';
import { OFFERING_TYPES } from '@pass-frontend/data-access/types';

export type OfferingID =
  | FacilityOffering['facilityId']
  | EventOffering['eventId'];

export interface UseChooseOfferings {
  chooseOffering: (offeringId: OfferingID) => void;
  chosenOfferingId: OfferingID | undefined;
  clearOfferingChoice: () => void;
  choosePin: (offeringId: OfferingID) => void;
  chosenPin: OfferingID | null;
}

export function useChooseOfferings(
  offeringType: OFFERING_TYPES,
): UseChooseOfferings {
  const dispatch = useDispatch();

  const chooseOffering = useCallback(
    (offeringID: OfferingID) => {
      switch (offeringType) {
        case OFFERING_TYPES.FACILITY:
          dispatch(Actions.updateChosenGymID(offeringID));
          break;
        case OFFERING_TYPES.EVENT:
          dispatch(Actions.updateChosenSocialEventID(offeringID));
          break;
      }
    },
    [offeringType, dispatch],
  );

  const clearOfferingChoice = useCallback(() => {
    switch (offeringType) {
      case OFFERING_TYPES.FACILITY:
        dispatch(Actions.clearChosenGym());
        break;
      case OFFERING_TYPES.EVENT:
        dispatch(Actions.clearChosenSocialEvent());
        break;
    }
  }, [offeringType, dispatch]);

  const chosenGymId = useSelector(selectChosenGymID);
  const chosenSocialEventId = useSelector(selectChosenSocialEventID);

  const chosenOfferingId = useMemo(() => {
    switch (offeringType) {
      case OFFERING_TYPES.FACILITY:
        return chosenGymId;
      case OFFERING_TYPES.EVENT:
        return chosenSocialEventId;
    }
  }, [offeringType, chosenGymId, chosenSocialEventId]);

  const choosePin = useCallback(
    (offeringID: OfferingID) => {
      switch (offeringType) {
        case OFFERING_TYPES.FACILITY:
          dispatch(Actions.updateChosenPin(offeringID));
          break;
        case OFFERING_TYPES.EVENT:
          dispatch(Actions.updateChosenPin(offeringID));
          break;
      }
    },
    [offeringType, dispatch],
  );

  const chosenGymPin = useSelector(selectChosenPin);

  const chosenPin = useMemo(() => {
    switch (offeringType) {
      case OFFERING_TYPES.FACILITY:
        return chosenGymPin;
      case OFFERING_TYPES.EVENT:
        return chosenGymPin;
      default:
        return null;
    }
  }, [offeringType, chosenGymPin]);

  return {
    chooseOffering,
    chosenOfferingId,
    clearOfferingChoice,
    choosePin,
    chosenPin,
  };
}

export default useChooseOfferings;
